import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import NoteAddOutlinedIcon from '@material-ui/icons/NoteAddOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { Alert } from '@material-ui/lab';
import {
  useGetPhoneRequestTemplatesQuery,
  useRequestContactPhoneNumberMutation,
} from '@src/reducers/contactsApi';
import { WhatsAppTemplate } from '@src/types';
import hasPlaceholders from '@src/utils/templatePlaceholders';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

dayjs.extend(relativeTime);

export interface RequestPhoneNumberDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (status: string) => void;
  contactId: number;
  bsuid: string;
  viaBotAccountId: number;
}

const RequestPhoneNumberDialog: FC<RequestPhoneNumberDialogProps> = ({
  open,
  onClose,
  onSuccess,
  contactId,
  bsuid,
  viaBotAccountId,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<WhatsAppTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (anchorRef.current) {
      const contactViewer = anchorRef.current.closest('.ContactViewer');
      const parent = contactViewer?.parentElement;
      if (parent) setPortalTarget(parent as HTMLElement);
    }
  }, []);

  const {
    data: templates = [],
    isFetching,
    isError,
  } = useGetPhoneRequestTemplatesQuery(
    { botAccountId: viaBotAccountId },
    { skip: !open || !viaBotAccountId }
  );

  const [requestPhoneNumber, { isLoading: isSending }] =
    useRequestContactPhoneNumberMutation();

  const filteredTemplates = useMemo(
    () =>
      templates
        // RN01f (ENG-1307): the button governs the list, not the template
        // type. Legacy approved templates may still carry variables the
        // console cannot assign — those stay out defensively.
        .filter(template => !hasPlaceholders(template.messageWithPlaceHolders))
        .filter(template =>
          template.name.toLowerCase().includes(searchQuery.toLowerCase())
        ),
    [templates, searchQuery]
  );

  const showEmptyState =
    !isFetching && (isError || filteredTemplates.length === 0);

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setSearchQuery('');
      setError('');
    }
  }, [open]);

  const handleSend = async () => {
    if (!selectedTemplate) return;

    setError('');
    try {
      const result = await requestPhoneNumber({
        contactId,
        bsuid,
        botAccountId: viaBotAccountId,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language || 'es',
      }).unwrap();
      onSuccess(result.waPhoneRequestStatus);
      onClose();
    } catch {
      setError('ContactForm.requestPhoneDialog.error');
    }
  };

  if (!open || !portalTarget) return <span ref={anchorRef} />;

  return createPortal(
    <Box className={classes.backdrop}>
      <Box className={classes.panel}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            <FormattedMessage id="ContactForm.requestPhoneDialog.title" />
          </Typography>
          <IconButton size="small" onClick={onClose} disabled={isSending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {!isFetching && templates.length > 0 && (
          <Box className={classes.searchBox}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={intl.formatMessage({
                id: 'ContactForm.requestPhoneDialog.search',
              })}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <Box className={classes.templateList}>
          {isFetching && (
            <Box className={classes.loadingBox}>
              <CircularProgress size={24} />
            </Box>
          )}

          {showEmptyState && (
            <Box className={classes.emptyState}>
              <NoteAddOutlinedIcon className={classes.emptyIcon} />
              <Typography className={classes.emptyTitle}>
                <FormattedMessage id="ContactForm.requestPhoneDialog.noTemplatesTitle" />
              </Typography>
              <Typography className={classes.emptySubtitle}>
                <FormattedMessage id="ContactForm.requestPhoneDialog.noTemplatesSubtitle" />
              </Typography>
            </Box>
          )}

          {!isFetching &&
            filteredTemplates.map(template => {
              const isSelected = selectedTemplate?.id === template.id;

              return (
                <Box
                  key={template.id}
                  className={`${classes.templateCard} ${
                    isSelected ? classes.templateCardSelected : ''
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <Typography className={classes.templateName}>
                    {template.name}
                  </Typography>
                  <Typography className={classes.templateMessage}>
                    {template.messageWithPlaceHolders}
                  </Typography>
                  <Typography className={classes.templateDate}>
                    {dayjs(template.createdAt).fromNow()}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        {error && (
          <Box px={2} pb={1}>
            <Alert severity="error">
              <FormattedMessage id={error} />
            </Alert>
          </Box>
        )}

        <Box className={classes.actions}>
          {!showEmptyState && (
            <Button
              variant="contained"
              color="primary"
              className={classes.sendButton}
              onClick={handleSend}
              disabled={!selectedTemplate || isSending || isFetching}
            >
              {isSending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FormattedMessage id="ContactForm.requestPhoneDialog.send" />
              )}
            </Button>
          )}
          <Button
            variant="outlined"
            className={classes.cancelButton}
            onClick={onClose}
            disabled={isSending}
          >
            <FormattedMessage id="ContactForm.requestPhoneDialog.cancel" />
          </Button>
        </Box>
      </Box>
    </Box>,
    portalTarget
  );
};

export default RequestPhoneNumberDialog;

const useStyles = makeStyles(theme => ({
  backdrop: {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'stretch',
    padding: 8,
  },
  panel: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 16px 12px',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    lineHeight: 1.3,
    flex: 1,
    paddingRight: 8,
  },
  searchBox: {
    padding: '0 16px 12px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  templateList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 8,
  },
  emptyIcon: {
    fontSize: 48,
    color: theme.palette.text.disabled,
  },
  emptyTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  templateCard: {
    padding: '12px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  templateCardSelected: {
    backgroundColor: theme.palette.action.selected,
    borderRadius: 8,
    padding: '12px 8px',
    marginBottom: 2,
    borderBottom: 'none',
  },
  templateName: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 2,
    color: theme.palette.text.primary,
  },
  templateMessage: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  templateDate: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    textAlign: 'right',
    marginTop: 4,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    padding: '10px 0',
    backgroundColor: theme.palette.background.paper,
  },
  sendButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    width: 237,
  },
  cancelButton: {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 8,
    width: 237,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
}));
